/**
 * OpenFeature Client
 *
 * Primary responsibilities:
 * - Initialize and configure the OpenFeature client
 * - Provide access for feature flag evaluation
 * - Set up Flagsmith provider
 *
 * Dependencies:
 * - OpenFeature SDK
 * - Flagsmith provider
 */

import { OpenFeature, Client, Logger } from '@openfeature/web-sdk'
import { FlagsmithProvider } from './flagsmith-provider'
import { captureException } from '@sentry/nextjs'
import { safeConsole } from '@/lib/utils/safeConsole'

// Feature flag environment variables
const FLAGSMITH_SERVER_API_KEY = process.env.NEXT_PUBLIC_FLAGSMITH_SERVER_API_KEY || ''
const FLAGSMITH_CLIENT_API_KEY = process.env.NEXT_PUBLIC_FLAGSMITH_CLIENT_API_KEY || ''
const FLAGSMITH_API_URL = process.env.NEXT_PUBLIC_FLAGSMITH_API_URL || undefined

// Track initialization status
let isInitialized = false
let initializationFailed = false
let initializationInProgress = false

// Define a simple logger to use for error handling - only log in development
const logger = {
  error: (message: string, ...args: any[]) => safeConsole.error(message, ...args),
  warn: (message: string, ...args: any[]) => safeConsole.warn(message, ...args),
  info: (message: string, ...args: any[]) => safeConsole.info(message, ...args),
  debug: (message: string, ...args: any[]) => safeConsole.debug(message, ...args)
}

/**
 * Initialize the OpenFeature SDK with the Flagsmith provider.
 * This should be called once at application startup.
 */
export async function initializeOpenFeature() {
  try {
    // Prevent duplicate initialization or retry if already failed
    if (isInitialized || initializationFailed || initializationInProgress) return

    initializationInProgress = true

    safeConsole.log('Initializing OpenFeature...')

    // Create and register the Flagsmith provider
    const provider = new FlagsmithProvider({
      serverSideKey: FLAGSMITH_SERVER_API_KEY,
      clientSideKey: FLAGSMITH_CLIENT_API_KEY,
      apiUrl: FLAGSMITH_API_URL
    })

    // Register the provider with OpenFeature
    await OpenFeature.setProvider(provider)

    // Mark as initialized
    isInitialized = true
    initializationInProgress = false

    safeConsole.log('OpenFeature initialized successfully')
  } catch (error) {
    // Mark initialization as failed to prevent continuous retries
    initializationFailed = true
    initializationInProgress = false
    safeConsole.error('Failed to initialize OpenFeature:', error)
    captureException(error)
  }
}

/**
 * Initialize OpenFeature for tests with provided mock keys.
 * This is for testing purposes only.
 */
export async function initializeOpenFeatureForTests() {
  try {
    // Create and register the Flagsmith provider with test keys
    const provider = new FlagsmithProvider({
      serverSideKey: 'test-server-key', // Use test keys
      clientSideKey: 'test-client-key' // Use test keys
    })

    // Register the provider with OpenFeature
    await OpenFeature.setProvider(provider)

    isInitialized = true
    return true
  } catch (error) {
    safeConsole.error('Failed to initialize test provider:', error)
    return false
  }
}

/**
 * Get the OpenFeature client for evaluating feature flags.
 * Ensures initialization is complete before returning the client.
 * @returns The OpenFeature client instance
 */
export function getOpenFeatureClient(): Client {
  // Try to initialize if not already initialized
  if (
    !isInitialized &&
    !initializationFailed &&
    !initializationInProgress &&
    typeof window !== 'undefined'
  ) {
    try {
      // Lazy initialize on first use instead of immediately
      initializeOpenFeature()
    } catch (error) {
      safeConsole.error('Error in getOpenFeatureClient:', error)
      // Mark initialization as failed to prevent continuous retries
      initializationFailed = true
    }
  }
  // Return client even if initialization failed - it will use default values
  return OpenFeature.getClient()
}

/**
 * Create a default OpenFeature client.
 * @returns The default OpenFeature client instance
 */
export function createDefaultClient(): Client {
  return getOpenFeatureClient()
}

/**
 * Export defaultClient getter for backward compatibility.
 * This ensures existing code continues to work while ensuring proper initialization.
 * Each method includes error handling to prevent app crashes.
 */
export const defaultClient = {
  getBooleanValue: async (flagKey: string, defaultValue: boolean, context?: any) => {
    try {
      const client = getOpenFeatureClient()
      return await client.getBooleanValue(flagKey, defaultValue, context)
    } catch (error) {
      logger.error(`Error getting boolean flag ${flagKey}:`, error)
      return defaultValue
    }
  },
  getStringValue: async (flagKey: string, defaultValue: string, context?: any) => {
    try {
      const client = getOpenFeatureClient()
      return await client.getStringValue(flagKey, defaultValue, context)
    } catch (error) {
      logger.error(`Error getting string flag ${flagKey}:`, error)
      return defaultValue
    }
  },
  getNumberValue: async (flagKey: string, defaultValue: number, context?: any) => {
    try {
      const client = getOpenFeatureClient()
      return await client.getNumberValue(flagKey, defaultValue, context)
    } catch (error) {
      logger.error(`Error getting number flag ${flagKey}:`, error)
      return defaultValue
    }
  },
  getObjectValue: async <T extends object>(flagKey: string, defaultValue: T, context?: any) => {
    try {
      const client = getOpenFeatureClient()
      return await client.getObjectValue<T>(flagKey, defaultValue, context)
    } catch (error) {
      logger.error(`Error getting object flag ${flagKey}:`, error)
      return defaultValue
    }
  }
}

/**
 * Logs the evaluation of a feature flag.
 * Used for tracking feature flag usage in analytics.
 *
 * @param key - The feature flag key
 * @param value - The evaluated value of the feature flag
 */
export const trackFlagEvaluation = (
  key: string,
  value: boolean | string | number | unknown
): void => {
  try {
    if (process.env.NODE_ENV === 'development') {
      return
    }

    // Convert any complex values to strings for analytics compatibility
    let trackableValue: boolean | string | number

    if (value === null || value === undefined) {
      trackableValue = 'null'
    } else if (typeof value === 'object' || Array.isArray(value)) {
      try {
        trackableValue = JSON.stringify(value)
      } catch (e) {
        trackableValue = '[Unstringifiable Object]'
      }
    } else if (
      typeof value !== 'boolean' &&
      typeof value !== 'string' &&
      typeof value !== 'number'
    ) {
      trackableValue = String(value)
    } else {
      trackableValue = value
    }

    // Add analytics tracking code here
    // e.g., posthog.capture('feature_flag_evaluated', { key, value: trackableValue })
  } catch (e) {
    // Silent catch - tracking should never affect application functionality
    logger.warn(`Failed to track feature flag ${key}:`, e)
  }
}

// Defer OpenFeature initialization to after page load
if (typeof window !== 'undefined') {
  try {
    // Use requestIdleCallback for non-critical initialization when browser is idle
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(
        () => {
          initializeOpenFeature().catch((error) => {
            console.error('Error initializing OpenFeature on client side:', error)
            initializationFailed = true
          })
        },
        { timeout: 2000 }
      )
    } else {
      // Fallback to setTimeout for browsers without requestIdleCallback
      setTimeout(() => {
        initializeOpenFeature().catch((error) => {
          console.error('Error initializing OpenFeature on client side:', error)
          initializationFailed = true
        })
      }, 100) // Short delay to prioritize UI rendering
    }
  } catch (error) {
    console.error('Unexpected error during OpenFeature initialization:', error)
    initializationFailed = true
  }
}
